import { acceptHMRUpdate, defineStore } from 'pinia'
import { upperFirst } from 'lodash-es'

import { useUserStore } from './user'

import { HttpError } from '@/helpers/errors'
import DeFiServicesService from '@/services/services/defi'

import type {
  DeFiAddressValue,
  DeFiService,
  DeFiServiceParent,
} from '@/types/services/defi'

export interface ServicesGroup {
  ids: string[]
  name: string
  logo_url: string | null
  blockchains: string[]
  services_ids: string[]
  provider_last_seen_at: string | null
  enabled_date: string | null
  disabled_date: string | null
  value_usd: number
  address_values: DeFiAddressValue[]
}

export interface DeFiServicesState {
  services: {
    data: DeFiService[]
    loading: boolean
    loaded: boolean
  }
  service_parents: {
    data: DeFiServiceParent[]
    loading: boolean
    loaded: boolean
  }
  updated_services: DeFiService[]
}

const groupServices = (servicesList: DeFiService[]) => {
  const services: { [key: string]: ServicesGroup } = {}
  servicesList.forEach((service) => {
    const blockchain = upperFirst(service.chain)
    const name = service.name.trim()
    if (name in services) {
      services[name].ids.push(service.id)
      services[name].blockchains.push(blockchain)
      services[name].value_usd += service.value_usd ?? 0
      services[name].address_values.push(...(service.address_values ?? []))
      if (!services[name].services_ids.includes(service.service_id)) {
        services[name].services_ids.push(service.service_id)
      }
      services[name].logo_url ??= service.logo_url
      if (
        service.provider_last_seen_at &&
        (!services[name].provider_last_seen_at ||
          service.provider_last_seen_at > services[name].provider_last_seen_at)
      ) {
        services[name].provider_last_seen_at = service.provider_last_seen_at
      }
    } else {
      services[name] = {
        ids: [service.id],
        name,
        logo_url: service.logo_url,
        blockchains: [blockchain],
        services_ids: [service.service_id],
        provider_last_seen_at: service.provider_last_seen_at ?? null,
        enabled_date: service.enabled_date,
        disabled_date: service.disabled_date,
        value_usd: service.value_usd ?? 0,
        address_values: [...(service.address_values ?? [])],
      }
    }
  })

  return Object.values(services).map((service) => {
    const valuesByChainAndAddress = new Map<string, DeFiAddressValue>()
    for (const value of service.address_values) {
      const key = JSON.stringify([
        value.chain ?? null,
        value.address,
        value.account_id,
      ])
      const current = valuesByChainAndAddress.get(key)
      if (current) {
        current.value_usd += value.value_usd
        if (value.last_observed_at < current.last_observed_at) {
          current.last_observed_at = value.last_observed_at
        }
      } else {
        valuesByChainAndAddress.set(key, { ...value })
      }
    }

    return {
      ...service,
      address_values: Array.from(valuesByChainAndAddress.values()).sort(
        (a, b) =>
          (a.chain ?? '').localeCompare(b.chain ?? '') ||
          (a.address ?? '').localeCompare(b.address ?? '') ||
          (a.account_name ?? '').localeCompare(b.account_name ?? '') ||
          a.account_id.localeCompare(b.account_id)
      ),
    }
  })
}

export const useServicesDeFiStore = defineStore('servicesDeFi', {
  state(): DeFiServicesState {
    return {
      services: {
        data: [],
        loading: false,
        loaded: false,
      },
      service_parents: {
        data: [],
        loading: false,
        loaded: false,
      },
      updated_services: [],
    }
  },

  actions: {
    async getAll() {
      this.$patch((state) => {
        state.services.loading = true
        state.services.loaded = false
        state.services.data = []
      })

      try {
        const response = await DeFiServicesService.getAll()
        this.$patch((state) => {
          state.services.data = response.data.data || []
          state.services.loading = false
          state.services.loaded = true
        })
      } catch (error) {
        this.services.loading = false
        throw new HttpError(error)
      }
    },

    async ensureLoaded() {
      if (this.services.loaded || this.services.loading) return
      await this.getAll()
    },

    async getParents() {
      this.$patch((state) => {
        state.service_parents.loading = true
        state.service_parents.loaded = false
        state.service_parents.data = []
      })

      try {
        const response = await DeFiServicesService.getParents()
        this.$patch((state) => {
          state.service_parents.data = response.data.data || []
          state.service_parents.loading = false
          state.service_parents.loaded = true
        })
      } catch (error) {
        this.service_parents.loading = false
        throw new HttpError(error)
      }
    },

    async ensureParentsLoaded() {
      if (this.service_parents.loaded || this.service_parents.loading) return
      await this.getParents()
    },

    async checkUpdatedServices(user_id: string) {
      this.updated_services = []

      try {
        const response = await DeFiServicesService.getUpdatedServices(user_id)
        this.updated_services = response.data.data || []
      } catch (error) {
        throw new HttpError(error)
      }
    },

    async setLastSeen(datetime: string) {
      const userStore = useUserStore()
      return userStore.saveUserSettings({
        last_seen_new_defi_services: datetime,
      })
    },

    async setAvailability(ids: string[], enable: boolean) {
      try {
        await DeFiServicesService.setAvailability(ids, enable)
        await this.getAll()
      } catch (error) {
        throw new HttpError(error)
      }
    },
  },

  getters: {
    groupedServices(state) {
      return groupServices(state.services.data)
    },

    currentGroupedServices(state) {
      return groupServices(
        state.services.data.filter(
          (service) => service.provider_available !== false
        )
      )
    },

    historicalGroupedServices(state) {
      return groupServices(
        state.services.data.filter(
          (service) => service.provider_available === false
        )
      )
    },

    serviceGroupsMap(): Map<string, ServicesGroup> {
      const groupsMap = new Map<string, ServicesGroup>()

      for (const group of this.groupedServices) {
        for (const serviceId of group.services_ids) {
          groupsMap.set(serviceId, group)
        }
      }

      return groupsMap
    },

    groupedUpdatedServices(state) {
      return groupServices(state.updated_services)
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useServicesDeFiStore, import.meta.hot))
}
